import { UserProfile, UserRole, UserNotificationPreferences, AppPermission } from '../types';

export type { AppPermission };

export type PermissionCategory = 'Core Access' | 'Operations' | 'Administration';

export interface PermissionDefinition {
  id: AppPermission;
  name: string;
  shortDescription: string;
  detailedDescription: string;
  category: PermissionCategory;
  isSensitive?: boolean;
}

/**
 * 4 Core Permissions automatically granted to all Standard Users and Super Admins
 */
export const CORE_PERMISSIONS: PermissionDefinition[] = [
  {
    id: 'dashboard',
    name: 'Dashboard Overview',
    shortDescription: 'Access the main volunteer hub overview and operational metrics',
    detailedDescription: 'View key performance statistics, stock summary cards, active reservation workloads, and pending notifications.',
    category: 'Core Access',
    isSensitive: false
  },
  {
    id: 'stock',
    name: 'Stock Inventory',
    shortDescription: 'Manage clothing items, sizes, quantities, and school assignments',
    detailedDescription: 'Add, edit, duplicate, adjust stock counts, toggle website visibility, and manage school clothing inventory.',
    category: 'Core Access',
    isSensitive: false
  },
  {
    id: 'reservations',
    name: 'Reservations & Orders',
    shortDescription: 'Process customer reservations and collection date requests',
    detailedDescription: 'Update order statuses (Being Prepared, Ready, Collected, Cancelled), review collection date change requests, print collection vouchers, and record internal customer notes.',
    category: 'Core Access',
    isSensitive: false
  },
  {
    id: 'messages',
    name: 'Customer Messages',
    shortDescription: 'Reply to parent enquiries and assign message threads to volunteers',
    detailedDescription: 'View customer contact submissions, send email replies, add internal staff notes, and sync inbound replies via IMAP.',
    category: 'Core Access',
    isSensitive: false
  }
];

export const CORE_PERMISSION_IDS: AppPermission[] = ['dashboard', 'stock', 'reservations', 'messages'];

/**
 * Optional Additional Permissions - Operations
 */
export const ADDITIONAL_PERMISSIONS_MANAGEMENT: PermissionDefinition[] = [
  {
    id: 'schools',
    name: 'School Management',
    shortDescription: 'Add, edit and manage participating schools.',
    detailedDescription: 'Manage academy crests, brand colors, school type, contact addresses, and enable/disable school filters across the shop.',
    category: 'Operations',
    isSensitive: true
  },
  {
    id: 'email_logs',
    name: 'Email System & Logs',
    shortDescription: 'Configure outbound email settings and view delivery logs.',
    detailedDescription: 'Customize sender details, test SMTP connectivity, view sent email logs, and review customer transaction receipts.',
    category: 'Operations',
    isSensitive: true
  },
  {
    id: 'news',
    name: 'News & Announcements',
    shortDescription: 'Create and publish news updates and announcements.',
    detailedDescription: 'Create and edit news articles, upload story images, and publish community updates to the public website.',
    category: 'Operations',
    isSensitive: false
  },
  {
    id: 'reports',
    name: 'Reports & Impact Analytics',
    shortDescription: 'View and export reports and uniform distribution analytics.',
    detailedDescription: 'Analyze community cost savings, garments saved from landfill, school breakdowns, and export operational CSV records.',
    category: 'Operations',
    isSensitive: false
  }
];

/**
 * Optional Additional Permissions - Administration
 */
export const ADDITIONAL_PERMISSIONS_SECURITY: PermissionDefinition[] = [
  {
    id: 'users',
    name: 'User Management',
    shortDescription: 'Create, edit, suspend and manage user accounts.',
    detailedDescription: 'Create volunteer user accounts, update profile details, trigger password reset emails, and activate or suspend team members.',
    category: 'Administration',
    isSensitive: true
  },
  {
    id: 'permissions',
    name: 'Permissions & Roles',
    shortDescription: 'Manage user permissions and access.',
    detailedDescription: 'Configure feature-by-feature access control profiles for every staff member and volunteer.',
    category: 'Administration',
    isSensitive: true
  },
  {
    id: 'settings',
    name: 'System Settings',
    shortDescription: 'Manage hub settings, schedules and operational configuration.',
    detailedDescription: 'Configure community centre venue information, opening day schedules, emergency closure banners, and maximum booking capacities.',
    category: 'Administration',
    isSensitive: true
  },
  {
    id: 'audit_logs',
    name: 'Audit Logs',
    shortDescription: 'View security and operational activity history.',
    detailedDescription: 'Inspect immutable records of reservation modifications, status updates, volunteer actions, and system events.',
    category: 'Administration',
    isSensitive: true
  }
];

/**
 * All active permission definitions in the system
 */
export const ALL_PERMISSIONS: PermissionDefinition[] = [
  ...CORE_PERMISSIONS,
  ...ADDITIONAL_PERMISSIONS_MANAGEMENT,
  ...ADDITIONAL_PERMISSIONS_SECURITY
];

export interface RolePreset {
  id: string;
  name: string;
  roleType: UserRole;
  description: string;
  permissions: AppPermission[];
  badgeColor: string;
}

/**
 * Standard two-tier role definitions
 */
export const ROLE_PRESETS: RolePreset[] = [
  {
    id: 'super_admin',
    name: 'Super Admin',
    roleType: 'super_admin',
    description: 'Full unrestricted access to every feature, setting and security control.',
    permissions: ALL_PERMISSIONS.map((p) => p.id),
    badgeColor: 'bg-amber-400 text-slate-950 border-amber-500'
  },
  {
    id: 'standard_user',
    name: 'Standard User',
    roleType: 'standard_user',
    description: 'Access to core operational areas. Additional access can be granted below.',
    permissions: [...CORE_PERMISSION_IDS],
    badgeColor: 'bg-blue-100 text-blue-800 border-blue-300'
  }
];

/**
 * Normalizes legacy roles into standard role names
 */
export function normalizeRole(role?: string | null): 'super_admin' | 'standard_user' {
  if (!role) return 'standard_user';
  const r = role.toLowerCase().trim();
  if (r === 'super_admin' || r === 'super admin' || r === 'admin' || r === 'administrator') {
    return 'super_admin';
  }
  return 'standard_user';
}

/**
 * Checks whether a given user has an administrator role (super_admin or admin).
 * Standard users, volunteers, customers, and unauthenticated visitors return false.
 */
export function isAdministrator(user: UserProfile | null | undefined): boolean {
  if (!user) return false;
  if (user.status === 'inactive' || user.status === 'suspended') return false;
  const role = user.role?.toLowerCase()?.trim();
  return (
    role === 'super_admin' ||
    role === 'admin' ||
    (user.email && user.email.trim().toLowerCase() === 'mrpostie1979@gmail.com') ||
    normalizeRole(user.role) === 'super_admin'
  );
}

/**
 * Returns human-readable role display name
 */
export function getRoleDisplayName(role?: string | null): string {
  const norm = normalizeRole(role);
  return norm === 'super_admin' ? 'Super Admin' : 'Standard User';
}

/**
 * Checks whether a given user profile has a specific permission.
 * Super Admin always returns true.
 * Standard Users always have core permissions (dashboard, stock, reservations, messages).
 * Additional permissions are checked against user.permissions (with alias support).
 */
export function hasPermission(
  user: UserProfile | null | undefined,
  permission: AppPermission | string
): boolean {
  if (!user) return false;

  // Account must be active
  if (user.status === 'inactive' || user.status === 'suspended') return false;

  // Designated Super Admin Email or super_admin role always has full unrestricted access
  if (
    user.role === 'super_admin' ||
    (user.email && user.email.trim().toLowerCase() === 'mrpostie1979@gmail.com') ||
    user.role === 'admin'
  ) {
    return true;
  }

  // Core permissions are ALWAYS granted to every standard user / volunteer
  if (['dashboard', 'stock', 'reservations', 'messages'].includes(permission)) {
    return true;
  }

  // For optional additional permissions, inspect user's permissions array
  const userPerms = Array.isArray(user.permissions) ? user.permissions : [];

  // Direct match
  if (userPerms.includes(permission as AppPermission)) {
    return true;
  }

  // Normalized alias checks
  if (permission === 'email_logs' || permission === 'emails') {
    return userPerms.includes('email_logs') || userPerms.includes('emails');
  }

  if (permission === 'users' || permission === 'volunteers') {
    return userPerms.includes('users') || userPerms.includes('volunteers');
  }

  if (permission === 'audit_logs' || permission === 'audit') {
    return userPerms.includes('audit_logs') || userPerms.includes('audit');
  }

  if (permission === 'news' || permission === 'homepage') {
    return userPerms.includes('news') || userPerms.includes('homepage');
  }

  return false;
}

/**
 * Checks if user has ANY of the specified permissions
 */
export function hasAnyPermission(
  user: UserProfile | null | undefined,
  permissions: (AppPermission | string)[]
): boolean {
  return permissions.some((p) => hasPermission(user, p));
}

/**
 * Checks if user has ALL of the specified permissions
 */
export function hasAllPermissions(
  user: UserProfile | null | undefined,
  permissions: (AppPermission | string)[]
): boolean {
  return permissions.every((p) => hasPermission(user, p));
}

/**
 * Returns default permissions array for a role
 */
export function getDefaultPermissionsForRole(role: UserRole | string): AppPermission[] {
  const norm = normalizeRole(role);
  if (norm === 'super_admin') {
    return ALL_PERMISSIONS.map((p) => p.id);
  }
  return [...CORE_PERMISSION_IDS];
}

/**
 * Normalizes notification preferences safely, migrating legacy orderUpdates to dateChangeRequests
 * while preserving both for complete backward compatibility.
 */
export function normalizeNotificationPreferences(
  prefs?: Partial<UserNotificationPreferences> | null
): {
  newOrders: boolean;
  dateChangeRequests: boolean;
  customerMessages: boolean;
  // Legacy aliases
  newOrder: boolean;
  orderUpdates: boolean;
  newCustomerMessage: boolean;
} {
  const p = prefs || {};
  const newOrders = p.newOrders !== undefined ? Boolean(p.newOrders) : (p.newOrder !== undefined ? Boolean(p.newOrder) : true);
  // Preserves existing orderUpdates: true -> dateChangeRequests: true; orderUpdates: false -> dateChangeRequests: false
  const dateChangeRequests = p.dateChangeRequests !== undefined ? Boolean(p.dateChangeRequests) : (p.orderUpdates !== undefined ? Boolean(p.orderUpdates) : true);
  const customerMessages = p.customerMessages !== undefined ? Boolean(p.customerMessages) : (p.newCustomerMessage !== undefined ? Boolean(p.newCustomerMessage) : true);

  return {
    newOrders,
    dateChangeRequests,
    customerMessages,
    newOrder: newOrders,
    orderUpdates: dateChangeRequests,
    newCustomerMessage: customerMessages
  };
}

/**
 * Returns whether a user is eligible to receive specific notification types based on their permissions.
 */
export function getUserNotificationEligibility(user: UserProfile | null | undefined): {
  canReceiveNewOrders: boolean;
  canReceiveDateChangeRequests: boolean;
  canReceiveCustomerMessages: boolean;
  canReceiveNewOrder: boolean;
  canReceiveOrderUpdates: boolean;
} {
  if (!user || user.status === 'inactive' || user.status === 'suspended') {
    return {
      canReceiveNewOrders: false,
      canReceiveDateChangeRequests: false,
      canReceiveCustomerMessages: false,
      canReceiveNewOrder: false,
      canReceiveOrderUpdates: false
    };
  }

  const hasOrdersAccess = hasPermission(user, 'reservations');
  const hasMessagesAccess = hasPermission(user, 'messages');

  return {
    canReceiveNewOrders: hasOrdersAccess,
    canReceiveDateChangeRequests: hasOrdersAccess,
    canReceiveCustomerMessages: hasMessagesAccess,
    canReceiveNewOrder: hasOrdersAccess,
    canReceiveOrderUpdates: hasOrdersAccess
  };
}

/**
 * Computes default notification preferences for a role and optional permissions list.
 * Enables all notification options that the user is eligible to receive by default.
 */
export function getDefaultNotificationPreferences(
  roleOrUser?: UserProfile | UserRole | string | null,
  customPermissions?: AppPermission[]
): UserNotificationPreferences {
  if (!roleOrUser) {
    return {
      newOrders: true,
      dateChangeRequests: true,
      customerMessages: true,
      newOrder: true,
      orderUpdates: true,
      newCustomerMessage: true
    };
  }

  let role: UserRole = 'standard_user';
  let perms: AppPermission[] = customPermissions || [];

  if (typeof roleOrUser === 'object') {
    role = roleOrUser.role || 'standard_user';
    perms = roleOrUser.permissions || customPermissions || [];
  } else {
    role = roleOrUser as UserRole;
  }

  const normRole = normalizeRole(role);
  const isSuper = normRole === 'super_admin';

  const hasOrders = isSuper || perms.includes('reservations') || CORE_PERMISSION_IDS.includes('reservations');
  const hasMessages = isSuper || perms.includes('messages') || CORE_PERMISSION_IDS.includes('messages');

  return {
    newOrders: hasOrders,
    dateChangeRequests: hasOrders,
    customerMessages: hasMessages,
    newOrder: hasOrders,
    orderUpdates: hasOrders,
    newCustomerMessage: hasMessages
  };
}

/**
 * Determines whether an active user should receive a specific notification event.
 * Checks active account status, permission authorization, and user preference independently.
 */
export function isUserEligibleAndSubscribed(
  user: UserProfile | null | undefined,
  notificationType: 'newOrders' | 'dateChangeRequests' | 'customerMessages' | 'newOrder' | 'orderUpdates' | 'newCustomerMessage'
): boolean {
  if (!user) return false;
  if (user.status === 'inactive' || user.status === 'suspended') return false;

  const eligibility = getUserNotificationEligibility(user);
  const norm = normalizeNotificationPreferences(user.notificationPreferences);

  if ((notificationType === 'newOrders' || notificationType === 'newOrder')) {
    if (!eligibility.canReceiveNewOrders) return false;
    return norm.newOrders;
  }

  if ((notificationType === 'dateChangeRequests' || notificationType === 'orderUpdates')) {
    if (!eligibility.canReceiveDateChangeRequests) return false;
    return norm.dateChangeRequests;
  }

  if ((notificationType === 'customerMessages' || notificationType === 'newCustomerMessage')) {
    if (!eligibility.canReceiveCustomerMessages) return false;
    return norm.customerMessages;
  }

  return false;
}
